import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { IMAGES } from "@/lib/images";
import api, { fileUrl } from "@/lib/api";
import { CTASection } from "@/pages/Home";

const FALLBACKS = [IMAGES.kitchen, IMAGES.bathroom, IMAGES.hero];

const PAIRS = [
  { before: "Shower Rebuild", after: "Walk-In Shower Tile", title: "Master Shower Rebuild" },
  { before: "Marble Shower — Installation Day", after: "Marble Guest Bath", title: "Marble Bathroom Transformation" },
];

function BeforeAfterSection({ items }) {
  const { t } = useTranslation();
  const find = (key) => {
    const item = items.find((i) => i.title.includes(key));
    return item ? fileUrl(item.image_path) : null;
  };
  const pairs = PAIRS.map((p) => ({ ...p, beforeSrc: find(p.before), afterSrc: find(p.after) })).filter((p) => p.beforeSrc && p.afterSrc);
  if (pairs.length === 0) return null;
  return (
    <section data-testid="before-after-section" className="py-16 md:py-24 px-6 md:px-12 bg-white border-b border-sandline">
      <div className="max-w-[1400px] mx-auto">
        <Reveal className="mb-12">
          <Kicker>{t("beforeAfter.kicker")}</Kicker>
          <h2 className="mt-4 font-heading font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl">{t("beforeAfter.title")}</h2>
          <p className="mt-4 text-slateblue max-w-lg">{t("beforeAfter.sub")}</p>
        </Reveal>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {pairs.map((p, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <BeforeAfterSlider
                before={p.beforeSrc}
                after={p.afterSrc}
                beforeLabel={t("beforeAfter.before")}
                afterLabel={t("beforeAfter.after")}
                title={p.title}
                testid={`before-after-${i}`}
              />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Portfolio({ lang }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const { data: portfolio, isLoading } = useQuery({
    queryKey: ["portfolio"],
    queryFn: async () => (await api.get("/portfolio")).data,
  });
  const items = portfolio || [];
  const categories = ["All", ...new Set(items.map((i) => i.category))];
  const visible = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <>
      <section data-testid="portfolio-hero" className="pt-40 pb-16 md:pt-52 md:pb-24 px-6 md:px-12 bg-navy text-offwhite">
        <div className="max-w-[1400px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <Kicker light>{t("portfolioPage.kicker")}</Kicker>
            <h1 className="mt-6 font-heading font-black uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
              {t("portfolioPage.title")}
            </h1>
            <p className="mt-6 text-offwhite/60 max-w-xl">{t("portfolioPage.sub")}</p>
          </motion.div>
        </div>
      </section>

      <BeforeAfterSection items={items} />

      <section data-testid="portfolio-grid-section" className="py-16 md:py-24 px-6 md:px-12 min-h-[50vh]">
        <div className="max-w-[1400px] mx-auto">
          <Reveal className="mb-12 flex flex-wrap gap-3">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                data-testid={`filter-${cat.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-5 py-2.5 text-xs font-bold uppercase tracking-widest border transition-[background-color,color,border-color] duration-200 ${
                  filter === cat ? "bg-navy text-offwhite border-navy" : "border-sandline text-slateblue hover:border-navy hover:text-navy"
                }`}
              >
                {cat === "All" ? t("portfolioPage.all") : cat}
              </button>
            ))}
          </Reveal>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2].map((i) => (
                <div key={i} className="aspect-[4/3] bg-offwhite-dark animate-pulse" />
              ))}
            </div>
          ) : visible.length === 0 ? (
            <p className="text-slateblue py-20 text-center" data-testid="portfolio-empty">{t("portfolioPage.empty")}</p>
          ) : (
            <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <AnimatePresence mode="popLayout">
                {visible.map((item, i) => (
                  <motion.button
                    layout
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.96 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                    onClick={() => setLightbox(item)}
                    data-testid={`portfolio-item-${i}`}
                    className="group text-left"
                  >
                    <div className="overflow-hidden relative">
                      <img
                        src={item.image_path ? fileUrl(item.image_path) : item.image_url || FALLBACKS[i % 3]}
                        alt={item.title}
                        loading="lazy"
                        className="w-full aspect-[4/3] object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/20 transition-colors duration-300" />
                    </div>
                    <div className="mt-4">
                      <span className="text-xs font-bold uppercase tracking-[0.2em] text-orange">{item.category}</span>
                      <h3 className="mt-1 font-heading font-bold text-lg tracking-tight">{item.title}</h3>
                    </div>
                  </motion.button>
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
            data-testid="portfolio-lightbox"
            className="fixed inset-0 z-[70] bg-navy/95 backdrop-blur-sm flex items-center justify-center p-6"
          >
            <button
              onClick={() => setLightbox(null)}
              data-testid="lightbox-close"
              className="absolute top-6 right-6 text-offwhite/70 hover:text-orange transition-colors duration-200"
              aria-label="Close"
            >
              <X size={32} />
            </button>
            <motion.div
              initial={{ scale: 0.92, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 20 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-4xl w-full"
            >
              <img
                src={lightbox.image_path ? fileUrl(lightbox.image_path) : lightbox.image_url || IMAGES.kitchen}
                alt={lightbox.title}
                className="w-full max-h-[70vh] object-contain"
              />
              <div className="mt-6 text-offwhite">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-orange">{lightbox.category}</span>
                <h3 className="mt-2 font-heading font-bold text-2xl tracking-tight">{lightbox.title}</h3>
                {lightbox.description && <p className="mt-2 text-offwhite/60">{lightbox.description}</p>}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CTASection lang={lang} />
    </>
  );
}
